import { FC, useRef } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { useCondensedCircleContentTracking } from '../tracking/sharedCondensedTracking';

import { PdpRespCondensedCircles_item$data } from './__generated__/PdpRespCondensedCircles_item.graphql';

import styles from './PdpRespCondensedCircles.scss';

type Props = {
    item: PdpRespCondensedCircles_item$data;
};

const PdpRespCondensedCirclesComponent: FC<Props> = ({ item }) => {
    const sectionVisibilityRef = useRef(null);
    const { condensedCircleContent, isArt, vertical } = item || {};

    const { handleSectionVisibilityChange, handleCircleClick } = useCondensedCircleContentTracking({
        vertical: vertical || '',
    });

    return (
        <div ref={sectionVisibilityRef}>
            <VisibilityTracker
                elementRef={sectionVisibilityRef}
                onVisibilityChange={handleSectionVisibilityChange}
            />
            <HeadingLevel>
                {Heading => (
                    <div className={styles.container}>
                        <Heading className={styles.header}>
                            {isArt ? (
                                <FormattedMessage
                                    defaultMessage="Shop By Artist"
                                    id="pdp.resp.condensed.shopByArtist"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Iconic Designs"
                                    id="pdp.resp.condensed.iconicDesigns"
                                />
                            )}
                        </Heading>
                        <HeadingLevel>
                            {() => (
                                <div className={styles.circlesContainer}>
                                    {condensedCircleContent?.[0]?.members?.map(member => {
                                        const anchorSource = member?.anchor?.source;
                                        if (
                                            anchorSource?.__typename === 'Creator' ||
                                            anchorSource?.__typename === 'IconicDesign'
                                        ) {
                                            const {
                                                displayName,
                                                browsePageImage,
                                                browsePageLinkData,
                                            } = anchorSource || {};
                                            return (
                                                <SeoLink
                                                    key={`${displayName}`}
                                                    dataTn={`condensed-pdp-circle-link-${displayName}`}
                                                    className={styles.circleLink}
                                                    linkData={{
                                                        path: browsePageLinkData?.path || null,
                                                        isLinkable:
                                                            browsePageLinkData?.isLinkable || false,
                                                    }}
                                                    onClick={() =>
                                                        handleCircleClick(displayName || '')
                                                    }
                                                >
                                                    <div className={styles.circleImgWrapper}>
                                                        <img
                                                            className={styles.circleImg}
                                                            src={browsePageImage || ''}
                                                            alt=""
                                                        />
                                                    </div>
                                                    <Heading className={styles.circleName}>
                                                        {displayName}
                                                    </Heading>
                                                </SeoLink>
                                            );
                                        }
                                        return null;
                                    })}
                                </div>
                            )}
                        </HeadingLevel>
                    </div>
                )}
            </HeadingLevel>
        </div>
    );
};

export const PdpRespCondensedCircles = createFragmentContainer(PdpRespCondensedCirclesComponent, {
    item: graphql`
        fragment PdpRespCondensedCircles_item on Item {
            isArt
            vertical
            condensedCircleContent {
                members {
                    anchor {
                        source {
                            __typename
                            ... on Creator {
                                displayName
                                browsePageImage
                                browsePageLinkData {
                                    isLinkable
                                    path
                                }
                            }
                            ... on IconicDesign {
                                displayName
                                browsePageImage
                                browsePageLinkData {
                                    isLinkable
                                    path
                                }
                            }
                        }
                    }
                }
            }
        }
    `,
});
